<template>
  <div class="swap-page">
    <div v-if="loadingInitial" class="swap-loading">
      <i class="fas fa-spinner fa-spin mr-2"></i> Carregando cancelamentos...
    </div>
    <template v-else>
    <ul class="nav swap-tabs">
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'request' }" @click="tab = 'request'">
          <i class="fas fa-paper-plane"></i> <span class="swap-tab-text">Solicitar</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'received' }" @click="tab = 'received'">
          <i class="fas fa-inbox"></i> <span class="swap-tab-text">Recebidas</span>
          <span v-if="received.length" class="swap-tab-badge">{{ received.length }}</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'accepted' }" @click="tab = 'accepted'">
          <i class="fas fa-handshake"></i> <span class="swap-tab-text">Aceitas</span>
          <span v-if="accepted.length" class="swap-tab-badge soft">{{ accepted.length }}</span>
        </button>
      </li>
      <li class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'mine' }" @click="tab = 'mine'">
          <i class="fas fa-list-ul"></i> <span class="swap-tab-text">Minhas</span>
          <span v-if="mine.length" class="swap-tab-badge soft">{{ mine.length }}</span>
        </button>
      </li>
      <li v-if="isApprover" class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'approvals' }" @click="tab = 'approvals'">
          <i class="fas fa-user-check"></i> <span class="swap-tab-text">Aprovações</span>
          <span v-if="pendingApprovals.length" class="swap-tab-badge danger">{{ pendingApprovals.length }}</span>
        </button>
      </li>
      <li v-if="isApprover" class="swap-tab-item">
        <button class="swap-tab" :class="{ active: tab === 'direct' }" @click="tab = 'direct'">
          <i class="fas fa-ban"></i> <span class="swap-tab-text">Cancelar troca</span>
        </button>
      </li>
    </ul>

    <div class="swap-pane" v-show="tab === 'request'">
      <h5 class="pane-title"><i class="fas fa-undo-alt mr-2 text-primary"></i> Solicitar cancelamento de troca</h5>
      <div class="card-body px-0">
        <small class="text-muted d-block mb-2">
          Escolha uma troca aprovada da qual você participa. O colega da troca precisa aceitar o cancelamento
          e, em seguida, a gestão precisa autorizá-lo.
        </small>
        <div v-if="hasLockedEligible" class="swap-hint mb-2">
          <i class="fas fa-info-circle mr-1"></i>
          Trocas marcadas com <span class="sched-pending"><i class="fas fa-clock mr-1"></i>Cancelamento em andamento</span>
          já possuem um pedido de cancelamento pendente. Acompanhe o andamento na aba <strong>Minhas</strong>.
        </div>

        <div v-if="eligibleLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!eligibleSwaps.length" class="swap-empty text-center py-3">
          <i class="far fa-calendar-times d-block mb-1" style="font-size:1.4rem;"></i>
          Você não possui trocas aprovadas que possam ser canceladas.
        </div>
        <template v-else>
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Solicitante da troca</th><th>Escala cedida</th><th>Colaborador</th><th>Escala do colaborador</th>
                  <th>Aprovada em</th><th class="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in eligiblePageRows" :key="s.id">
                  <td>{{ s.requesterName }}</td>
                  <td>{{ dutyDay(s.sourceSchedule) }}<div class="swap-range">{{ shiftRangeLabel(s.sourceSchedule) }}</div></td>
                  <td>{{ s.targetName }}</td>
                  <td>{{ dutyDay(s.targetSchedule) }}<div class="swap-range">{{ shiftRangeLabel(s.targetSchedule) }}</div></td>
                  <td>{{ s.decidedAt ? formatDateTime(s.decidedAt) : '—' }}</td>
                  <td class="text-center">
                    <span v-if="isSwapLocked(s.id)" class="sched-pending">
                      <i class="fas fa-clock mr-1"></i>Cancelamento em andamento
                    </span>
                    <button v-else class="btn btn-sm btn-outline-danger" :disabled="loading" @click="submit(s)">
                      <i class="fas fa-undo-alt mr-1"></i> Solicitar cancelamento
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <swap-pagination :page="eligiblePage" :pages="eligiblePages" :total="eligibleSwaps.length"
                           :per-page="10" :disabled="eligibleLoading"
                           @change="p => { eligiblePage = p; }" />
        </template>
      </div>
    </div>

    <div v-if="isApprover" class="swap-pane" v-show="tab === 'direct'">
      <h5 class="pane-title"><i class="fas fa-ban mr-2 text-danger"></i> Cancelar troca aprovada</h5>
      <div class="card-body px-0">
        <small class="text-muted d-block mb-2">
          O cancelamento é aplicado imediatamente, sem aceite do colaborador nem nova aprovação:
          as escalas voltam para os colaboradores originais.
        </small>

        <div class="swap-step-head"><span class="step-num">1</span> Escolha a troca</div>
        <div class="row">
          <div class="col-12 col-md-4 mb-2">
            <label class="small text-muted mb-0">Solicitante</label>
            <input type="text" class="form-control form-control-sm" v-model="directFilters.requester" placeholder="Filtrar...">
          </div>
          <div class="col-12 col-md-4 mb-2">
            <label class="small text-muted mb-0">Colaborador</label>
            <input type="text" class="form-control form-control-sm" v-model="directFilters.colleague" placeholder="Filtrar...">
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th></th><th>Solicitante</th><th>Escala dele</th><th>Colaborador</th><th>Escala do colaborador</th>
                <th>Aprovada por</th><th>Aprovada em</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="directLoading">
                <td colspan="7" class="text-center py-3 text-muted">
                  <i class="fas fa-spinner fa-spin mr-2"></i> Carregando trocas...
                </td>
              </tr>
              <tr v-else-if="!directRows.length">
                <td colspan="7" class="text-center py-3 text-muted">Nenhuma troca aprovada encontrada.</td>
              </tr>
              <tr v-for="s in directRows" v-else :key="s.id"
                  class="swap-selectable" :class="{ 'swap-row-selected': Number(directForm.work_schedule_swap_request_id) === Number(s.id), 'swap-row-locked': isSwapLocked(s.id) }"
                  @click="toggleDirect(s)">
                <td>
                  <i v-if="Number(directForm.work_schedule_swap_request_id) === Number(s.id)" class="fas fa-check-circle sched-on"></i>
                  <span v-else-if="isSwapLocked(s.id)" class="sched-pending" title="Já existe um pedido de cancelamento pendente">
                    <i class="fas fa-clock"></i>
                  </span>
                  <i v-else class="far fa-circle text-muted"></i>
                </td>
                <td>{{ s.requesterName }}</td>
                <td>{{ dutyDay(s.sourceSchedule) }}<div class="swap-range">{{ shiftRangeLabel(s.sourceSchedule) }}</div></td>
                <td>{{ s.targetName }}</td>
                <td>{{ dutyDay(s.targetSchedule) }}<div class="swap-range">{{ shiftRangeLabel(s.targetSchedule) }}</div></td>
                <td>{{ s.approverName }}</td>
                <td>{{ s.decidedAt ? formatDateTime(s.decidedAt) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <swap-pagination :page="directMeta.page" :pages="directMeta.pages" :total="directMeta.count"
                         :per-page="10" :disabled="directLoading"
                         @change="p => fetchDirectSwaps(p)" />

        <template v-if="directSelected">
          <div class="swap-step-head mt-3"><span class="step-num">2</span> Confirme o cancelamento</div>
          <div class="summary-bar mb-2">
            <i class="fas fa-info-circle mr-2"></i>
            A troca entre <strong>{{ directSelected.requesterName }}</strong> ({{ dutyDay(directSelected.sourceSchedule) }})
            e <strong>{{ directSelected.targetName }}</strong> ({{ dutyDay(directSelected.targetSchedule) }}) será desfeita.
          </div>
          <div class="row">
            <div class="col-12 col-md-8 mb-2">
              <label class="small text-muted mb-0">Justificativa (opcional)</label>
              <textarea class="form-control" rows="2" v-model="directForm.approver_justification" maxlength="1000"
                        placeholder="Ex.: troca registrada por engano"></textarea>
            </div>
          </div>
          <div class="custom-control custom-checkbox mb-3">
            <input type="checkbox" class="custom-control-input" id="cancel-direct-notify" v-model="directForm.notify">
            <label class="custom-control-label" for="cancel-direct-notify">Avisar os colaboradores por e-mail</label>
          </div>
        </template>

        <button class="btn btn-danger" :disabled="loading || !canSubmitDirect" @click="submitDirect">
          <i class="fas fa-ban mr-1"></i> Efetivar cancelamento
        </button>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'received'">
      <h5 class="pane-title"><i class="fas fa-inbox mr-2 text-warning"></i> Pedidos de cancelamento recebidos</h5>
      <div class="card-body px-0">
        <div v-if="receivedLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!receivedList.length" class="swap-empty text-center py-3">
          <i class="far fa-check-circle d-block mb-1" style="font-size:1.4rem;"></i>
          Nenhum pedido de cancelamento aguardando sua resposta.
        </div>
        <template v-else>
          <div v-for="c in rows(receivedList)" :key="c.id" class="recv-card">
            <div class="recv-info">
              <div><strong>{{ c.requesterName }}</strong> quer cancelar a troca</div>
              <div class="text-muted small">
                <strong>{{ c.swap.requesterName }}</strong> — {{ dutyDay(c.swap.sourceSchedule) }}
                <span class="swap-range">({{ shiftRangeLabel(c.swap.sourceSchedule) }})</span>
                <i class="fas fa-exchange-alt mx-1"></i>
                <strong>{{ c.swap.targetName }}</strong> — {{ dutyDay(c.swap.targetSchedule) }}
                <span class="swap-range">({{ shiftRangeLabel(c.swap.targetSchedule) }})</span>
                <span v-if="c.requestedReason"> — “{{ c.requestedReason }}”</span>
              </div>
            </div>
            <div class="recv-actions">
              <button class="btn btn-sm btn-success mr-1" :disabled="loading" @click="accept(c.id)">
                <i class="fas fa-check mr-1"></i> Aceitar
              </button>
              <button class="btn btn-sm btn-outline-danger" :disabled="loading" @click="decline(c.id)">
                <i class="fas fa-times mr-1"></i> Recusar
              </button>
            </div>
          </div>
          <swap-pagination :page="receivedMeta.page" :pages="receivedMeta.pages" :total="receivedMeta.count"
                           :per-page="10" :disabled="receivedLoading"
                           @change="p => goToTabPage('received', p)" />
        </template>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'accepted'">
      <h5 class="pane-title"><i class="fas fa-handshake mr-2 text-success"></i> Cancelamentos que você aceitou</h5>
      <div class="card-body px-0">
        <div v-if="acceptedLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!acceptedList.length" class="swap-empty text-center py-3">Nenhum cancelamento aceito aguardando autorização.</div>
        <template v-else>
          <div v-for="c in rows(acceptedList)" :key="c.id" class="recv-card">
            <div class="recv-info">
              <div><strong>{{ c.requesterName }}</strong> — cancelamento aceito</div>
              <div class="text-muted small">
                <strong>{{ c.swap.requesterName }}</strong> — {{ dutyDay(c.swap.sourceSchedule) }}
                <span class="swap-range">({{ shiftRangeLabel(c.swap.sourceSchedule) }})</span>
                <i class="fas fa-exchange-alt mx-1"></i>
                <strong>{{ c.swap.targetName }}</strong> — {{ dutyDay(c.swap.targetSchedule) }}
                <span class="swap-range">({{ shiftRangeLabel(c.swap.targetSchedule) }})</span>
              </div>
            </div>
            <span class="badge align-self-center" :class="statusClass(c.status)">{{ statusLabel(c.status) }}</span>
          </div>
          <swap-pagination :page="acceptedMeta.page" :pages="acceptedMeta.pages" :total="acceptedMeta.count"
                           :per-page="10" :disabled="acceptedLoading"
                           @change="p => goToTabPage('accepted', p)" />
        </template>
      </div>
    </div>

    <div class="swap-pane" v-show="tab === 'mine'">
      <h5 class="pane-title"><i class="fas fa-paper-plane mr-2 text-primary"></i> Meus pedidos de cancelamento</h5>
      <div class="card-body px-0">
        <div v-if="mineLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!mineList.length" class="swap-empty text-center py-3">Você não tem pedidos de cancelamento.</div>
        <template v-else>
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Colaborador</th><th>Troca</th><th>Data e Hora da solicitação</th><th>Motivo</th>
                  <th>Status</th><th>Justificativa da gestão</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in rows(mineList)" :key="c.id">
                  <td>{{ c.targetName }}</td>
                  <td>
                    {{ c.swap.requesterName }}: {{ dutyDay(c.swap.sourceSchedule) }}
                    <div class="swap-range">{{ c.swap.targetName }}: {{ dutyDay(c.swap.targetSchedule) }}</div>
                  </td>
                  <td>{{ c.createdAt ? formatDateTime(c.createdAt) : '—' }}</td>
                  <td>{{ c.requestedReason || '—' }}</td>
                  <td><span class="badge" :class="statusClass(c.status)">{{ statusLabel(c.status) }}</span></td>
                  <td>{{ c.approverJustification || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <swap-pagination :page="mineMeta.page" :pages="mineMeta.pages" :total="mineMeta.count"
                           :per-page="10" :disabled="mineLoading"
                           @change="p => goToTabPage('mine', p)" />
        </template>
      </div>
    </div>

    <div v-if="isApprover" class="swap-pane" v-show="tab === 'approvals'">
      <div class="pane-title d-flex justify-content-between align-items-center">
        <span><i class="fas fa-user-check mr-2 text-danger"></i> Cancelamentos aguardando autorização</span>
        <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="fetchCancellations">
          <i class="fas fa-sync-alt mr-1"></i> Atualizar
        </button>
      </div>
      <div class="btn-group btn-group-sm mb-3" role="group">
        <button type="button" class="btn" :class="approvalsView === 'pending' ? 'btn-primary' : 'btn-outline-primary'"
                @click="approvalsView = 'pending'">
          Pendentes <span v-if="pendingApprovals.length" class="badge badge-light" style="color: #000;">{{ pendingApprovals.length }}</span>
        </button>
        <button type="button" class="btn" :class="approvalsView === 'history' ? 'btn-primary' : 'btn-outline-primary'"
                @click="approvalsView = 'history'">
          Histórico
        </button>
      </div>
      <div v-show="approvalsView === 'pending'" class="card-body px-0">
        <div v-if="approvalsLoading" class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin mr-2"></i> Carregando...
        </div>
        <div v-else-if="!approvalsList.length" class="swap-empty text-center py-3">
          <i class="fas fa-check-circle text-success d-block mb-1" style="font-size:1.4rem;"></i>
          Nenhum cancelamento aguardando autorização.
        </div>
        <template v-else>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Solicitante</th><th>Data e Hora da solicitação</th><th>Colaborador</th>
                <th>Troca</th><th>Motivo</th><th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in rows(approvalsList)" :key="c.id">
                <td>{{ c.requesterName }}</td>
                <td>{{ c.createdAt ? formatDateTime(c.createdAt) : '—' }}</td>
                <td>{{ c.targetName }}</td>
                <td>
                  {{ c.swap.requesterName }}: {{ dutyDay(c.swap.sourceSchedule) }}
                  <div class="swap-range">{{ shiftRangeLabel(c.swap.sourceSchedule) }}</div>
                  {{ c.swap.targetName }}: {{ dutyDay(c.swap.targetSchedule) }}
                  <div class="swap-range">{{ shiftRangeLabel(c.swap.targetSchedule) }}</div>
                </td>
                <td>{{ c.requestedReason || '—' }}</td>
                <td class="text-center">
                  <div class="swap-actions-cell">
                    <button class="btn btn-sm btn-outline-success" :disabled="loading" @click="approve(c.id)">
                      <i class="fas fa-check"></i> Aprovar
                    </button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="loading" @click="reject(c.id)">
                      <i class="fas fa-times"></i> Reprovar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <swap-pagination :page="approvalsMeta.page" :pages="approvalsMeta.pages" :total="approvalsMeta.count"
                         :per-page="10" :disabled="approvalsLoading"
                         @change="p => goToTabPage('approvals', p)" />
        </template>
      </div>
      <div v-show="approvalsView === 'history'" class="card-body px-0">
        <div class="swap-history-toolbar">
          <span class="text-muted small">{{ historyMeta.total }} cancelamento(s)</span>
          <button v-if="historyFiltersActive" type="button" class="btn btn-sm btn-link px-0"
                  @click="clearHistoryFilters">
            <i class="fas fa-times mr-1"></i> Limpar filtros
          </button>
        </div>
        <div class="table-responsive">
          <table class="table table-hover mb-0 swap-history-table">
            <thead>
              <tr>
                <th>Solicitante</th><th>Data e Hora da solicitação</th><th>Colaborador</th><th>Troca</th>
                <th>Status</th><th>Justificativa</th><th>Decidido por</th><th>Data e Hora</th>
              </tr>
              <tr class="swap-filter-row">
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.requester"
                         placeholder="Filtrar...">
                </th>
                <th></th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.colleague"
                         placeholder="Filtrar...">
                </th>
                <th></th>
                <th>
                  <select class="form-control form-control-sm" v-model="historyFilters.status">
                    <option value="">Todos</option>
                    <option value="approved">Autorizado</option>
                    <option value="rejected">Reprovado</option>
                  </select>
                </th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.reason"
                         placeholder="Filtrar...">
                </th>
                <th>
                  <input type="text" class="form-control form-control-sm" v-model="historyFilters.approver"
                         placeholder="Filtrar...">
                </th>
                <th>
                  <input type="date" class="form-control form-control-sm swap-date-input"
                         v-model="historyFilters.decided_date">
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="historyLoading">
                <td colspan="8" class="text-center py-3 text-muted">
                  <i class="fas fa-spinner fa-spin mr-2"></i> Carregando histórico...
                </td>
              </tr>
              <tr v-else-if="!history.length">
                <td colspan="8" class="text-center py-3 text-muted">
                  {{ historyFiltersActive ? 'Nenhum cancelamento corresponde aos filtros.' : 'Nenhum cancelamento decidido ainda.' }}
                </td>
              </tr>
              <tr v-for="c in rows(history)" v-else :key="c.id">
                <td>{{ c.requesterName }}</td>
                <td>{{ c.createdAt ? formatDateTime(c.createdAt) : '—' }}</td>
                <td>{{ c.targetName }}</td>
                <td>
                  {{ c.swap.requesterName }}: {{ dutyDay(c.swap.sourceSchedule) }}
                  <div class="swap-range">{{ c.swap.targetName }}: {{ dutyDay(c.swap.targetSchedule) }}</div>
                </td>
                <td><span class="badge" :class="statusClass(c.status)">{{ statusLabel(c.status) }}</span></td>
                <td>{{ c.approverJustification || '—' }}</td>
                <td>{{ c.approverName }}</td>
                <td>{{ c.decidedAt ? formatDateTime(c.decidedAt) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="historyMeta.last_page > 1" class="d-flex justify-content-between align-items-center mt-2">
          <button class="btn btn-sm btn-outline-secondary" :disabled="historyMeta.current_page <= 1 || historyLoading"
                  @click="fetchHistory(historyMeta.current_page - 1)">
            <i class="fas fa-chevron-left mr-1"></i> Anterior
          </button>
          <span class="text-muted small">{{ historyMeta.current_page }} / {{ historyMeta.last_page }}</span>
          <button class="btn btn-sm btn-outline-secondary" :disabled="historyMeta.current_page >= historyMeta.last_page || historyLoading"
                  @click="fetchHistory(historyMeta.current_page + 1)">
            Próxima <i class="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import swal from 'sweetalert';
import SwapPagination from '@/components/SwapPagination';

const PER_PAGE = 10;

export default {
  name: 'SwapCancellationPanel',
  components: { SwapPagination },
  data() {
    return {
      loading: false,
      loadingInitial: true,
      tab: 'request',
      defaultTabSet: false,
      myId: null,
      cancellations: [],      
      approvedSwaps: [],
      eligibleLoading: false,
      eligiblePage: 1,
      directSwaps: [],
      directMeta: { page: 1, pages: 1, count: 0 },
      directLoading: false,
      directLoaded: false,
      directDebounce: null,
      directFilters: { requester: '', colleague: '' },
      directForm: {
        work_schedule_swap_request_id: null,
        approver_justification: '',
        notify: false,
      },
      approvalsView: 'pending',
      historyFilters: {
        requester: '', colleague: '', status: '', reason: '', approver: '', decided_date: '',
      },
      history: [],
      historyMeta: { current_page: 1, last_page: 1, total: 0 },
      historyLoading: false,
      historyLoaded: false,
      historyDebounce: null,
      receivedList: [],
      receivedMeta: { page: 1, pages: 1, count: 0 },
      receivedLoading: false,
      receivedLoaded: false,
      acceptedList: [],
      acceptedMeta: { page: 1, pages: 1, count: 0 },
      acceptedLoading: false,
      acceptedLoaded: false,
      mineList: [],
      mineMeta: { page: 1, pages: 1, count: 0 },
      mineLoading: false,
      mineLoaded: false,
      approvalsList: [],
      approvalsMeta: { page: 1, pages: 1, count: 0 },
      approvalsLoading: false,
      approvalsLoaded: false,
    };
  },
  computed: {
    isApprover() {
      const user = this.$store.state.user;
      return !!(user && Number(user.is_admin) !== 3);
    },
    canSelfApprove() {
      const u = this.$store.state.user;
      return !!(u && [1, 2].includes(Number(u.is_admin)));
    },
    me() {
      return this.myId || (this.$store.state.user && this.$store.state.user.id) || null;
    },
    received() {
      if (!this.me) return [];
      return this.cancellations.filter(r => Number(r.target_user_id) === Number(this.me) && r.status === 'pending_peer');
    },
    accepted() {
      if (!this.me) return [];
      return this.cancellations.filter(r => Number(r.target_user_id) === Number(this.me) && r.status === 'pending_approval');
    },
    mine() {
      if (!this.me) return [];
      return this.cancellations.filter(r => Number(r.requested_by_user_id) === Number(this.me));
    },
    pendingApprovals() {
      return this.cancellations.filter(r => r.status === 'pending_approval'
        && (this.canSelfApprove || Number(r.requested_by_user_id) !== Number(this.me)));
    },
    
    lockedSwapIds() {
      const ids = new Set();
      this.cancellations.forEach((r) => {
        if (!['pending_peer', 'pending_approval'].includes(r.status)) return;
        const id = r.work_schedule_swap_request_id || (this.swapOf(r) && this.swapOf(r).id);
        if (id) ids.add(Number(id));
      });
      return ids;
    },
    eligibleSwaps() {
      if (!this.me) return [];
      return this.approvedSwaps
        .filter(s => s.status === 'approved'
          && (Number(s.requested_by_user_id) === Number(this.me) || Number(s.target_user_id) === Number(this.me)))
        .map(s => this.swapView(s));
    },
    eligiblePages() {
      return Math.max(1, Math.ceil(this.eligibleSwaps.length / PER_PAGE));
    },
    eligiblePageRows() {
      const start = (this.eligiblePage - 1) * PER_PAGE;
      return this.eligibleSwaps.slice(start, start + PER_PAGE);
    },
    hasLockedEligible() {
      return this.eligibleSwaps.some(s => this.isSwapLocked(s.id));
    },
    directRows() {
      return this.directSwaps.map(s => this.swapView(s));
    },
    directSelected() {
      const id = Number(this.directForm.work_schedule_swap_request_id);
      if (!id) return null;
      return this.directRows.find(s => Number(s.id) === id) || null;
    },
    canSubmitDirect() {
      return !!this.directForm.work_schedule_swap_request_id
        && !this.isSwapLocked(this.directForm.work_schedule_swap_request_id);
    },
    historyFiltersActive() {
      return Object.values(this.historyFilters).some(v => v !== '' && v !== null);
    },
  },
  watch: {
    tab(newTab) {
      if (newTab === 'received' && !this.receivedLoaded) this.fetchTabList('received');
      if (newTab === 'accepted' && !this.acceptedLoaded) this.fetchTabList('accepted');
      if (newTab === 'mine' && !this.mineLoaded) this.fetchTabList('mine');
      if (newTab === 'approvals' && this.approvalsView === 'pending' && !this.approvalsLoaded) this.fetchTabList('approvals');
      if (newTab === 'direct' && !this.directLoaded) this.fetchDirectSwaps(1);
    },
    approvalsView(view) {
      if (view === 'history' && !this.historyLoaded) this.fetchHistory(1);
      if (view === 'pending' && !this.approvalsLoaded) this.fetchTabList('approvals');
    },
    eligiblePages(pages) {
      if (this.eligiblePage > pages) this.eligiblePage = pages;
    },
    'directFilters.requester'() { this.queueDirectFetch(); },
    'directFilters.colleague'() { this.queueDirectFetch(); },
    'historyFilters.status'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.decided_date'() { if (this.approvalsView === 'history') this.fetchHistory(1); },
    'historyFilters.requester'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.colleague'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.approver'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
    'historyFilters.reason'() { if (this.approvalsView === 'history') this.queueHistoryFetch(); },
  },
  mounted() {
    this.loadMe().then(() => {
      this.fetchCancellations();
      this.fetchApprovedSwaps();
    });
  },
  methods: {
    api(path) {
      return this.$store.state.api + path;
    },
    loadMe() {
      return axios.post(this.api('me'), { token: this.$store.state.token })
        .then(r => { this.myId = (r.data && r.data.id) || null; })
        .catch(() => { this.myId = (this.$store.state.user && this.$store.state.user.id) || null; });
    },   
    userName(u) {
      return (u && u.name) || '—';
    },
    swapOf(r) {
      if (!r) return null;
      return r.work_schedule_swap_request || r.swap_request || r.swap || null;
    },
    swapView(s) {
      const swap = s || {};
      return {
        id: swap.id,
        status: swap.status,
        requesterName: this.userName(swap.requested_by),
        targetName: this.userName(swap.target_user),
        approverName: this.userName(swap.approver),
        sourceSchedule: swap.work_schedule || null,
        targetSchedule: swap.target_work_schedule || null,
        decidedAt: swap.responded_at || swap.updated_at || null,
      };
    },
    cancelView(r) {
      const c = r || {};
      const swap = this.swapOf(c) || {};      
      const swapData = {
        ...swap,
        work_schedule: swap.work_schedule || c.work_schedule || null,
        target_work_schedule: swap.target_work_schedule || c.target_work_schedule || null,
      };
      return {
        id: c.id,
        status: c.status,
        swapId: c.work_schedule_swap_request_id || swap.id || null,
        requesterName: this.userName(c.requested_by),
        targetName: this.userName(c.target_user),
        approverName: this.userName(c.approver),
        requestedReason: c.requested_reason || '',
        approverJustification: c.approver_justification || '',
        createdAt: c.created_at || null,
        peerRespondedAt: c.peer_responded_at || null,
        decidedAt: c.responded_at || c.updated_at || null,
        swap: this.swapView(swapData),
      };
    },
    rows(list) {
      return (list || []).map(r => this.cancelView(r));
    },
    normalizePage(p) {
      if (!p || typeof p !== 'object') return { data: [], page: 1, pages: 1, count: 0 };
      if (Array.isArray(p)) return { data: p, page: 1, pages: 1, count: p.length };
      if (typeof p.current_page === 'number') {
        return { data: p.data || [], page: p.current_page, pages: p.last_page || 1, count: p.total || 0 };
      }
      return {
        data: p.data || [],
        page: typeof p.page === 'number' ? p.page + 1 : 1,
        pages: p.pages || 1,
        count: p.count || 0,
      };
    },

    dutyDay(s) {
      const base = s && (s.shift_start_time || s.duty_day);
      return base ? moment(base).format('DD/MM/YYYY') : '—';
    },
    formatDateTime(v) {
      const m = moment(v);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
    },
    shiftRangeLabel(s) {
      if (!s || !s.shift_start_time || !s.shift_end_time) return 'Dia inteiro';
      const start = moment(s.shift_start_time);
      const end = moment(s.shift_end_time);
      if (!start.isValid() || !end.isValid()) return 'Dia inteiro';
      return start.isSame(end, 'day')
        ? `${start.format('DD/MM HH:mm')} – ${end.format('HH:mm')}`
        : `${start.format('DD/MM HH:mm')} → ${end.format('DD/MM HH:mm')}`;
    },
    statusLabel(s) {
      return {
        pending_peer: 'Aguardando aceite do colega',
        pending_approval: 'Aguardando autorização da gestão',
        approved: 'Cancelamento autorizado',
        rejected: 'Cancelamento reprovado',
      }[s] || s;
    },
    statusClass(s) {
      return {
        pending_peer: 'badge-warning',
        pending_approval: 'badge-info',
        approved: 'badge-success',
        rejected: 'badge-danger',
      }[s] || 'badge-secondary';
    },
    isSwapLocked(id) {
      return !!id && this.lockedSwapIds.has(Number(id));
    },

    fetchCancellations() {
      axios.get(this.api('work_schedule_swap_cancellation_requests'))
        .then(r => {
          const d = r.data && r.data.data;
          this.cancellations = Array.isArray(d) ? d : ((d && d.data) || []);
          if (!this.defaultTabSet) {
            this.defaultTabSet = true;
            if (this.isApprover && this.pendingApprovals.length) this.tab = 'approvals';
            else if (this.received.length) this.tab = 'received';
          }
          if (this.approvalsView === 'history' && this.historyLoaded) {
            this.fetchHistory(this.historyMeta.current_page);
          }
          this.refreshTabLists();
        })
        .catch(() => { this.cancellations = []; })
        .finally(() => { this.loadingInitial = false; });
    },
    fetchApprovedSwaps() {
      this.eligibleLoading = true;
      axios.get(this.api('work_schedule_swap_requests'), { params: { status: 'approved' } })
        .then(r => { this.approvedSwaps = (r.data && r.data.data) || []; })
        .catch(() => { this.approvedSwaps = []; })
        .finally(() => { this.eligibleLoading = false; });
    },
    fetchDirectSwaps(page) {
      this.directLoading = true;
      const params = { page: page || 1, per_page: PER_PAGE, status: 'approved' };
      if (this.directFilters.requester) params.requester = this.directFilters.requester.trim();
      if (this.directFilters.colleague) params.colleague = this.directFilters.colleague.trim();
      axios.get(this.api('work_schedule_swap_requests/history'), { params })
        .then((r) => {
          const p = this.normalizePage(r.data && r.data.data);
          this.directSwaps = p.data;
          this.directMeta = { page: p.page, pages: p.pages, count: p.count };
          this.directLoaded = true;
        })
        .catch(() => {
          this.directSwaps = [];
          this.directMeta = { page: 1, pages: 1, count: 0 };
        })
        .finally(() => { this.directLoading = false; });
    },
    queueDirectFetch() {
      if (this.directDebounce) clearTimeout(this.directDebounce);
      this.directDebounce = setTimeout(() => { this.fetchDirectSwaps(1); }, 350);
    },
    clearHistoryFilters() {
      this.historyFilters = {
        requester: '', colleague: '', status: '', reason: '', approver: '', decided_date: '',
      };
      this.fetchHistory(1);
    },
    fetchHistory(page) {
      this.historyLoading = true;
      const f = this.historyFilters;
      const params = { page: page || 1 };
      if (f.status) params.status = f.status;
      if (f.requester) params.requester = f.requester.trim();
      if (f.colleague) params.colleague = f.colleague.trim();
      if (f.approver) params.approver = f.approver.trim();
      if (f.reason) params.reason = f.reason.trim();
      if (f.decided_date) params.decided_date = f.decided_date;
      axios.get(this.api('work_schedule_swap_cancellation_requests/history'), { params })
        .then((r) => {
          const p = this.normalizePage(r.data && r.data.data);
          this.history = p.data;
          this.historyMeta = { current_page: p.page, last_page: p.pages, total: p.count };
          this.historyLoaded = true;
        })
        .catch(() => {
          this.history = [];
          this.historyMeta = { current_page: 1, last_page: 1, total: 0 };
        })
        .finally(() => { this.historyLoading = false; });
    },
    queueHistoryFetch() {
      if (this.historyDebounce) clearTimeout(this.historyDebounce);
      this.historyDebounce = setTimeout(() => { this.fetchHistory(1); }, 350);
    },
    fetchTabList(view) {
      const metaKey = `${view}Meta`;
      const listKey = `${view}List`;
      const loadingKey = `${view}Loading`;
      const page = this[metaKey].page || 1;
      this[loadingKey] = true;
      const params = { paginated: 1, limit: PER_PAGE, page, view };
      axios.get(this.api('work_schedule_swap_cancellation_requests/tab'), { params })
        .then((r) => {
          const p = this.normalizePage(r.data && r.data.data);
          this[listKey] = p.data;
          this[metaKey] = { page: p.page, pages: p.pages, count: p.count };
          this[`${view}Loaded`] = true;
        })
        .catch(() => {
          this[listKey] = [];
          this[metaKey] = { page: 1, pages: 1, count: 0 };
        })
        .finally(() => { this[loadingKey] = false; });
    },
    goToTabPage(view, page) {
      this[`${view}Meta`].page = page;
      this.fetchTabList(view);
    },
    refreshTabLists() {
      if (this.receivedLoaded) this.fetchTabList('received');
      if (this.acceptedLoaded) this.fetchTabList('accepted');
      if (this.mineLoaded) this.fetchTabList('mine');
      if (this.approvalsLoaded) this.fetchTabList('approvals');
    },
    refreshAll() {
      this.fetchCancellations();
      this.fetchApprovedSwaps();
      if (this.directLoaded) this.fetchDirectSwaps(this.directMeta.page);
    },

    promptText(opts) {
      const input = document.createElement('textarea');
      input.rows = 3;
      input.maxLength = 1000;
      input.placeholder = opts.placeholder || '';
      input.className = 'form-control';
      return swal({
        title: opts.title,
        text: opts.text,
        icon: opts.icon || 'warning',
        content: input,
        buttons: ['Cancelar', opts.confirm],
        dangerMode: !!opts.danger,
      }).then(confirmed => (confirmed ? { value: (input.value || '').trim() } : null));
    },
    toggleDirect(s) {
      if (this.isSwapLocked(s.id)) {
        this.$message('Atenção', 'Esta troca já possui um pedido de cancelamento pendente.', 'warning');
        return;
      }
      const id = Number(this.directForm.work_schedule_swap_request_id);
      this.directForm.work_schedule_swap_request_id = id === Number(s.id) ? null : s.id;
    },
    resetDirectForm() {
      this.directForm.work_schedule_swap_request_id = null;
      this.directForm.approver_justification = '';
      this.directForm.notify = false;
    },
    submit(s) {
      this.promptText({
        title: 'Solicitar cancelamento',
        text: `Troca entre ${s.requesterName} (${this.dutyDay(s.sourceSchedule)}) e ${s.targetName} (${this.dutyDay(s.targetSchedule)}). Informe o motivo do cancelamento:`,
        placeholder: 'Motivo do cancelamento',
        confirm: 'Enviar solicitação',
      }).then((res) => {
        if (!res) return;
        this.loading = true;
        axios.post(this.api('work_schedule_swap_cancellation_requests'), {
          work_schedule_swap_request_id: s.id,
          requested_reason: res.value || null,
        }).then(() => {
          this.$message('Sucesso', 'Solicitação de cancelamento enviada.', 'success');
          this.refreshAll();
        }).catch(e => {
          this.$message('Erro', e?.response?.data?.message || 'Erro ao solicitar o cancelamento.', 'error');
        }).finally(() => { this.loading = false; });
      });
    },
    submitDirect() {
      this.loading = true;
      axios.post(this.api('work_schedule_swap_cancellation_requests/direct'), {
        work_schedule_swap_request_id: this.directForm.work_schedule_swap_request_id,
        approver_justification: this.directForm.approver_justification || null,
        notify: this.directForm.notify,
      }).then(() => {
        this.$message('Sucesso', 'Troca cancelada.', 'success');
        this.resetDirectForm();
        this.refreshAll();
      }).catch(e => {
        this.$message('Erro', e?.response?.data?.message || 'Erro ao cancelar a troca.', 'error');
      }).finally(() => { this.loading = false; });
    },
    accept(id) {
      this.loading = true;
      axios.post(this.api(`work_schedule_swap_cancellation_requests/${id}/accept`))
        .then(() => { this.$message('Sucesso', 'Cancelamento aceito. Aguardando autorização da gestão.', 'success'); this.refreshAll(); })
        .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao aceitar.', 'error'))
        .finally(() => { this.loading = false; });
    },
    decline(id) {
      this.promptText({
        title: 'Recusar cancelamento',
        text: 'Informe o motivo da recusa (opcional):',
        placeholder: 'Motivo da recusa',
        confirm: 'Recusar',
        danger: true,
      }).then((res) => {
        if (!res) return;
        this.loading = true;
        axios.post(this.api(`work_schedule_swap_cancellation_requests/${id}/decline`), { reason: res.value || null })
          .then(() => { this.$message('Sucesso', 'Cancelamento recusado.', 'success'); this.refreshAll(); })
          .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao recusar.', 'error'))
          .finally(() => { this.loading = false; });
      });
    },
    approve(id) {
      this.promptText({
        title: 'Autorizar cancelamento',
        text: 'A troca será desfeita e as escalas voltarão aos colaboradores originais. Justificativa (opcional):',
        placeholder: 'Justificativa',
        icon: 'info',
        confirm: 'Aprovar',
      }).then((res) => {
        if (!res) return;
        this.loading = true;
        axios.post(this.api(`work_schedule_swap_cancellation_requests/${id}/approve`), {
          approver_justification: res.value || null,
        })
          .then(() => { this.$message('Sucesso', 'Cancelamento autorizado.', 'success'); this.refreshAll(); })
          .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao aprovar.', 'error'))
          .finally(() => { this.loading = false; });
      });
    },
    reject(id) {
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 1000;
      input.placeholder = 'Justificativa da reprovação (obrigatória)';
      input.className = 'form-control';

      swal({
        title: 'Reprovar cancelamento',
        text: 'Informe a justificativa da reprovação:',
        icon: 'warning',
        content: input,
        buttons: ['Cancelar', 'Reprovar'],
        dangerMode: true,
      }).then((confirmed) => {
        if (!confirmed) return;
        const reason = (input.value || '').trim();
        if (!reason) {
          this.$message('Atenção', 'A justificativa é obrigatória.', 'warning');
          return;
        }
        this.loading = true;
        axios.post(this.api(`work_schedule_swap_cancellation_requests/${id}/reject`), { approver_justification: reason })
          .then(() => { this.$message('Sucesso', 'Cancelamento reprovado.', 'success'); this.refreshAll(); })
          .catch(e => this.$message('Erro', e?.response?.data?.message || 'Erro ao reprovar.', 'error'))
          .finally(() => { this.loading = false; });
      });
    },
  },
};
</script>

<style scoped>
.swap-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  border-bottom: 1px solid #e6e6ef;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  list-style: none;
  padding-left: 0;
}
.swap-tab-item { margin: 0; }
.swap-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid transparent;
  background: transparent;
  color: #6e6b7b;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 8px;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.swap-tab:hover { background: #eaf1fa; color: #15488A; }
.swap-tab.active {
  background: #15488A;
  color: #fff;
  box-shadow: 0 4px 10px rgba(21, 72, 138, 0.35);
}
.swap-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ea5455;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}
.swap-tab-badge.soft { background: #b9b9c3; }
.swap-tab-badge.danger { background: #ea5455; }
.swap-tab.active .swap-tab-badge { background: #fff; color: #15488A; }

.swap-page { min-height: 340px; }
.swap-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 340px;
  color: #6e6b7b;
  font-size: 0.95rem;
}
.swap-pane { animation: swapFade 0.15s ease; }
@keyframes swapFade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.pane-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.swap-step-head {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin: 0.25rem 0 0.75rem;
}
.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #15488A;
  color: #fff;
  font-size: 0.75rem;
  margin-right: 0.5rem;
}

.sched-pending {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  color: #b06a00;
  background: #fbf1df;
  border: 1px solid #f0d68a;
  border-radius: 10px;
  padding: 1px 7px;
  white-space: nowrap;
}
.sched-on { color: #28c76f; }

.swap-range { color: #6e6b7b; font-size: 0.78rem; }
td .swap-range { display: block; }

.swap-selectable { cursor: pointer; }
.swap-row-selected > td { background: #e3edf8; }
.swap-row-locked { opacity: 0.6; cursor: not-allowed; }

.swap-history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.swap-history-table th { vertical-align: middle; }
.swap-filter-row th {
  padding: 0.35rem 0.5rem 0.5rem;
  background: #f7f9fc;
  border-top: 0;
  font-weight: 400;
}
.swap-filter-row .form-control-sm { min-width: 7rem; }
.swap-date-input {
  min-width: 0;
  max-width: 100%;
}

.swap-empty {
  color: #a0a0b2;
  font-size: 0.85rem;
  border: 1px dashed #e0e0ec;
  border-radius: 0.5rem;
  padding: 0.75rem;
  text-align: center;
}

.summary-bar {
  background: #eafaf1;
  border: 1px solid #c3ecd5;
  color: #1f7a4d;
  border-radius: 0.5rem;
  padding: 0.6rem 0.85rem;
  font-size: 0.9rem;
}

.swap-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  background: #eef4fb;
  border: 1px solid #cfe0f2;
  color: #2c4a6b;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  line-height: 1.45;
}
.swap-hint .sched-pending { margin: 0 0.15rem; }

.recv-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid #e6e6ef;
  border-radius: 0.5rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.5rem;
}
.recv-actions { white-space: nowrap; }

.swap-actions-cell {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: center;
}

@media (max-width: 767.98px) {
  .recv-card { flex-direction: column; align-items: stretch; }
  .swap-actions-cell { flex-direction: column; align-items: stretch; }
}
</style>
